import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommandPalette, t } from '@kanbios/ui';
import { api, unwrap } from '../lib/api';
import { useDisplayScope } from './ScopePicker';

/**
 * Recherche et actions Ctrl K (SPEC-002 RG-002-4 to RG-002-6): each query goes
 * to GET /search (rights, then displayed scope, 120 ms debounce) and the DSM
 * palette shows the answer as given (`filter={false}`). Recent objects while
 * nothing is typed.
 */
export function SearchPalette({ open, onOpen, onClose }: { open: boolean; onOpen: () => void; onClose: () => void }) {
  const navigate = useNavigate();
  const scope = useDisplayScope();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim().slice(0, 100)), 120);
    return () => clearTimeout(timer);
  }, [query]);
  // Each opening starts from an empty query.
  useEffect(() => {
    if (!open) { setQuery(''); setDebounced(''); }
  }, [open]);
  const search = useQuery({
    queryKey: ['search', scope, debounced],
    queryFn: async () => unwrap(await api.maintenance.GET('/search', { params: { query: { q: debounced, scope } } })),
    enabled: open,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
  const recent = useQuery({
    queryKey: ['search-recent', scope],
    queryFn: async () => unwrap(await api.maintenance.GET('/search/recent', { params: { query: { scope } } })),
    enabled: open,
  });
  // « Récents » holds objects: an action opened from the palette is not remembered.
  const go = (path: string, remember: boolean) => {
    onClose();
    if (remember) void api.maintenance.POST('/search/recent', { body: { path } });
    navigate(path);
  };
  type Item = { label: string; description?: string; icon: string; keywords: string; target: { path: string } };
  const toItem = (remember: boolean) => (i: Item) => ({ label: i.label, description: i.description, icon: i.icon, keywords: i.keywords, onSelect: () => go(i.target.path, remember) });
  const groups = [
    ...(!query.trim() && recent.data?.items.length ? [{ label: t('search.recent'), limit: 10, items: recent.data.items.map(toItem(true)) }] : []),
    // RG-002 « Palette »: Actions, then the objects, 5 per group (the server cuts).
    ...(search.data?.groups ?? []).map(g => ({ label: g.label, items: g.items.map(toItem(g.id !== 'actions')) })),
  ];
  return (
    <CommandPalette
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      placeholder={search.isError ? t('search.failed') : t('search.placeholder')}
      groups={groups}
      filter={false}
      onQueryChange={setQuery}
      loading={query.trim() !== debounced || search.isFetching}
    />
  );
}
